import { useMemo, useState } from "react";

function normalizeArticlesJson(parsed) {
  if (Array.isArray(parsed)) return parsed;
  if (parsed && Array.isArray(parsed.articles)) return parsed.articles;
  if (parsed && Array.isArray(parsed.pages)) return parsed.pages;
  if (parsed && Array.isArray(parsed.publications)) return parsed.publications;
  return [];
}

function countWords(value) {
  return String(value || "")
    .replace(/<[^>]+>/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;
}

function cleanSlug(value) {
  return String(value || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\-/]+/g, "-")
    .replace(/\/+ /g, "/")
    .replace(/\/+/g, "/")
    .replace(/^-+|-+$/g, "")
    .replace(/\/-+|-+\//g, "/")
    .replace(/^\/+|\/+$/g, "");
}

function downloadSampleJson() {
  const sample = {
    publish_now: false,
    upsert: true,
    articles: [
      {
        slug: "retirement-income-planning-business-owners",
        title: "Retirement Income Planning for Business Owners",
        meta_description: "Learn how business owners can coordinate retirement income, liquidity, insurance, and tax-aware planning before exiting or slowing down.",
        h1: "Retirement Income Planning for Business Owners",
        excerpt: "A practical guide for business owners who want better retirement income options and more control over liquidity.",
        body: "<p>Business owners often have wealth concentrated in the company, real estate, or illiquid assets. A coordinated plan can help organize income, liquidity, insurance, and exit timing.</p><h2>Start with liquidity</h2><p>Before choosing a product or strategy, define how much cash needs to remain accessible for opportunities, taxes, emergencies, and family goals.</p><h2>Coordinate the pieces</h2><p>Retirement income planning may involve cash reserves, insured strategies, qualified plans, business succession, and estate planning conversations with the client&apos;s professional team.</p>",
        keywords: ["retirement income", "business owners", "liquidity planning"],
        cta_label: "Book a Consultation",
        cta_url: "/#contact",
        canonical_url: "",
        og_image: "/logo.png",
        author: "Lion Pride Financial",
        status: "draft",
        publish_at: ""
      }
    ]
  };

  const blob = new Blob([JSON.stringify(sample, null, 2)], { type: "application/json;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "lion-pride-sample-article-import.json";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function summarizeArticle(article, index) {
  const title = article.title || article.seo_title || article.h1 || `Article ${index + 1}`;
  const slug = cleanSlug(article.slug || "");
  const meta = article.meta_description || article.description || "";
  const bodyWords = countWords(article.body || article.content || "");
  const status = article.status || (article.publish_now ? "published" : "draft");
  return { title, slug, meta, bodyWords, status };
}

export default function BulkSeoPageImportWidget() {
  const [fileName, setFileName] = useState("");
  const [articles, setArticles] = useState([]);
  const [rawJson, setRawJson] = useState(null);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [publishNow, setPublishNow] = useState(true);
  const [upsert, setUpsert] = useState(true);

  const summary = useMemo(() => {
    const rows = articles.map(summarizeArticle);
    return {
      rows,
      count: rows.length,
      ready: rows.filter((r) => r.slug && r.title && r.meta.length >= 70 && r.bodyWords >= 80).length,
      seoReady: rows.filter((r) => r.slug && r.title.length >= 45 && r.title.length <= 65 && r.meta.length >= 120 && r.meta.length <= 160 && r.bodyWords >= 300).length,
    };
  }, [articles]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    setResults(null);
    setMessage("");
    setArticles([]);
    setRawJson(null);
    if (!file) return;
    setFileName(file.name);

    try {
      if (!file.name.toLowerCase().endsWith(".json")) {
        throw new Error("Please choose a .json file.");
      }
      if (file.size > 4 * 1024 * 1024) {
        throw new Error("JSON file is too large. Keep it under 4 MB.");
      }
      const text = await file.text();
      const parsed = JSON.parse(text);
      const list = normalizeArticlesJson(parsed);
      if (!list.length) {
        throw new Error("No articles found. Use an array or an object with an articles array.");
      }
      setRawJson(parsed);
      setArticles(list);
      setMessage(`Loaded ${list.length} article${list.length === 1 ? "" : "s"} from ${file.name}.`);
    } catch (err) {
      setMessage(err.message || "Unable to read JSON file.");
    }
  };

  const importArticles = async () => {
    if (!articles.length) {
      setMessage("Choose a JSON file first.");
      return;
    }
    if (!window.confirm(`Import ${articles.length} article(s)? ${publishNow ? "They will publish immediately." : "They will keep their JSON status."}`)) return;

    setLoading(true);
    setMessage("");
    setResults(null);
    try {
      const payload = {
        action: "bulk_import",
        publish_now: publishNow,
        upsert,
        source_file: fileName,
        articles: normalizeArticlesJson(rawJson),
      };
      const res = await fetch("/api/page_bulk_import.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (_) {
        throw new Error(`Endpoint did not return JSON. First response text: ${text.slice(0, 180)}`);
      }
      if (!res.ok || !data.success) throw new Error(data.error || data.detail || "Bulk import failed");
      setResults(data);
      setMessage(`Import complete: ${data.imported || 0} imported, ${data.updated || 0} updated, ${data.published || 0} published, ${data.failed || 0} failed.`);
    } catch (err) {
      setMessage(err.message || "Bulk import failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white text-gray-900 rounded-2xl shadow p-6 w-full">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-5">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Bulk JSON Article Import</h2>
          <p className="text-sm text-gray-600 mt-1">
            Upload a .json file with many SEO publications and publish them in one batch.
          </p>
        </div>
        <div className="flex flex-col gap-3 md:items-end">
          <button
            type="button"
            onClick={downloadSampleJson}
            className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100"
          >
            Download sample .json
          </button>
          <div className="text-sm bg-gray-100 border rounded-xl px-4 py-3 min-w-[190px]">
            <div className="font-semibold">Loaded Articles</div>
            <div className="text-2xl font-bold text-blue-700">{summary.count}</div>
            <div className="text-xs text-gray-600">{summary.seoReady} SEO-ready · {summary.ready} valid enough</div>
          </div>
        </div>
      </div>

      {message && (
        <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
          {message}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-4">
          <label className="block">
            <span className="text-sm font-medium">Article JSON file</span>
            <input type="file" accept="application/json,.json" className="mt-1 w-full border rounded-lg px-3 py-2" onChange={onFile} />
            <span className="block mt-1 text-xs text-gray-500">Expected root: an array, or an object with articles, pages, or publications. Use the sample download above for the preferred format.</span>
          </label>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="flex items-center gap-2 text-sm border rounded-lg p-3 bg-gray-50">
              <input type="checkbox" checked={publishNow} onChange={(e) => setPublishNow(e.target.checked)} />
              Publish every imported article now
            </label>
            <label className="flex items-center gap-2 text-sm border rounded-lg p-3 bg-gray-50">
              <input type="checkbox" checked={upsert} onChange={(e) => setUpsert(e.target.checked)} />
              Update existing pages with matching slugs
            </label>
          </div>

          <div className="flex gap-2">
            <button disabled={loading || !articles.length} onClick={importArticles} className="bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white px-4 py-2 rounded-lg" type="button">
              {loading ? "Importing..." : "Import JSON Articles"}
            </button>
            <button type="button" className="border px-4 py-2 rounded-lg" onClick={() => { setArticles([]); setRawJson(null); setResults(null); setFileName(""); setMessage(""); }}>
              Clear
            </button>
          </div>

          <div className="rounded-xl border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-900">
            <strong>Compliance reminder:</strong> imported articles should remain educational. Avoid guarantees, tax promises, exaggerated return language, or personalized financial advice.
          </div>
        </div>

        <div className="border rounded-xl p-4 bg-gray-50 text-sm">
          <h3 className="font-semibold mb-3">JSON fields accepted</h3>
          <ul className="space-y-1 text-gray-700">
            <li><code>slug</code></li>
            <li><code>title</code> or <code>seo_title</code></li>
            <li><code>meta_description</code></li>
            <li><code>h1</code></li>
            <li><code>excerpt</code></li>
            <li><code>body</code></li>
            <li><code>keywords</code></li>
            <li><code>cta_label</code>, <code>cta_url</code></li>
            <li><code>canonical_url</code>, <code>og_image</code></li>
            <li><code>status</code>, <code>publish_at</code></li>
          </ul>
        </div>
      </div>

      {summary.rows.length > 0 && (
        <div className="mt-6 overflow-x-auto border rounded-xl">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100 text-gray-700">
              <tr>
                <th className="text-left p-3">Slug</th>
                <th className="text-left p-3">Title</th>
                <th className="text-left p-3">Meta</th>
                <th className="text-left p-3">Words</th>
                <th className="text-left p-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {summary.rows.slice(0, 80).map((row, idx) => (
                <tr key={`${row.slug}-${idx}`} className="border-t">
                  <td className="p-3 font-mono text-xs">/{row.slug || "missing-slug"}/</td>
                  <td className="p-3">{row.title}</td>
                  <td className="p-3">{row.meta.length}</td>
                  <td className="p-3">{row.bodyWords}</td>
                  <td className="p-3">{row.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {results?.results?.length > 0 && (
        <div className="mt-6 overflow-x-auto border rounded-xl">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100 text-gray-700">
              <tr>
                <th className="text-left p-3">Slug</th>
                <th className="text-left p-3">Action</th>
                <th className="text-left p-3">Published</th>
                <th className="text-left p-3">Message</th>
              </tr>
            </thead>
            <tbody>
              {results.results.map((row, idx) => (
                <tr key={`${row.slug}-${idx}`} className="border-t">
                  <td className="p-3 font-mono text-xs">/{row.slug || "—"}/</td>
                  <td className="p-3">{row.action || "—"}</td>
                  <td className="p-3">{row.published ? "yes" : "no"}</td>
                  <td className="p-3">{row.error || row.detail || row.url || "OK"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
